import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { auth, functions } from '../firebase/config';
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  User,
  UserCredential,
  IdTokenResult,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import PageLoader from './PageLoader';
import './auth.css';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface UserCredentials {
  email: string;
  password: string;
}

interface Affiliation {
  schoolID: string | null;
  role: string;
}

interface SetClaimsInput {
  uid: string;
  role: string;
  schoolID: string | null;
  setAsDefault: boolean;
}

interface SetClaimsOutput {
  message: string;
  schoolID: string | null;
  role: string;
}

interface CreateUserInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  schoolID: string | null;
  setAsDefault: boolean;
}

interface CreateUserSocialInput {
  idToken: string;
  firstName: string;
  lastName: string;
  provider: string;
  role: string;
  setAsDefault: boolean;
  schoolID: string | null;
}

interface CreateUserOutput {
  message: string;
  uid: string;
  schoolID: string | null;
  role: string;
}

interface FetchError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
  code?: string;
}

const isAffiliationArray = (value: unknown): value is Affiliation[] => {
  return Array.isArray(value) && value.every((item) => typeof item === 'object' && item !== null && 'role' in item && typeof item.role === 'string' && 'schoolID' in item && (typeof item.schoolID === 'string' || item.schoolID === null));
};

const PORTAL = 'school_staff';
const PORTAL_DISPLAY_NAME = 'School Staff';
const BASE_FUNCTION_URL = 'https://us-central1-cbsms-da9ce.cloudfunctions.net/createUser';

// Clear stale authentication data before login
const clearStaleAuthData = (currentUid?: string): void => {
  try {
    const cachedAuth = localStorage.getItem('cachedAuth');
    if (cachedAuth && currentUid) {
      try {
        const parsed = JSON.parse(cachedAuth);
        if (parsed.uid && parsed.uid !== currentUid) {
          console.log('Clearing cached auth for different user:', parsed.uid);
          localStorage.removeItem('cachedAuth');
        }
      } catch (e) {
        localStorage.removeItem('cachedAuth');
      }
    } else if (cachedAuth && !currentUid) {
      localStorage.removeItem('cachedAuth');
    }
    console.log('Stale auth data cleared if needed');
  } catch (error) {
    console.error('Error clearing stale auth data:', error);
  }
};

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoaded, setIsLoaded] = useState<boolean>(true);
  const [loginType, setLoginType] = useState<'login' | 'signup'>('login');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hostname = window.location.hostname || '';
    const allowed = hostname.includes('cbsms-da9ce.web.app') || hostname === 'localhost' || hostname.includes('netlify.app') || hostname.includes('sonoma.school');
    if (!allowed) {
      setError('Login is only accessible from authorized domains');
      navigate('/landing-page');
    }
  }, [navigate]);
  const [userCredentials, setUserCredentials] = useState<UserCredentials>({ email: '', password: '' });
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>('');
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const handleCredentials = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setUserCredentials((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateInputs = (): void => {
    if (!userCredentials.email || !userCredentials.password) {
      throw new Error('Email and password must not be empty');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userCredentials.email)) {
      throw new Error('Invalid email format');
    }
    if (userCredentials.password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }
    if (loginType === 'signup') {
      if (!firstName || !lastName) {
        throw new Error('Please fill in first name and last name');
      }
      if (firstName.length > 50 || lastName.length > 50) {
        throw new Error('First and last names must be 50 characters or less');
      }
    }
  };

  const verifyAndSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      validateInputs();
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA is not ready. Please try again later.');
      }
      const recaptchaToken = await executeRecaptcha(loginType === 'login' ? 'login' : 'signup');

      if (loginType === 'login') {
        const verifyResp = await fetch(`${BASE_FUNCTION_URL}/verify-recaptcha`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recaptchaToken, expectedAction: 'login' }),
        });
        if (!verifyResp.ok) {
          const text = await verifyResp.text();
          let data: any = {};
          try { data = JSON.parse(text); } catch {}
          throw new Error(data?.message || `reCAPTCHA verification failed (${verifyResp.status})`);
        }
        const vr = await verifyResp.json();
        if (!vr?.success) throw new Error('reCAPTCHA verification failed');
        await handleSignIn();
      } else {
        await handleSignUp(recaptchaToken);
      }
    } catch (err: unknown) {
      const error = err as FetchError;
      console.error('Auth process error:', error);
      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            setError('Invalid email address format.');
            break;
          case 'auth/user-disabled':
            setError('This user account has been disabled.');
            break;
          case 'auth/user-not-found':
            setError('No user found with this email.');
            break;
          case 'auth/wrong-password':
            setError('Incorrect password. Please try again.');
            break;
          case 'auth/email-already-in-use':
            setError('This email is already registered. Please use a different email or sign in.');
            break;
          default:
            setError(error.message || 'Authentication error');
        }
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.message.includes('Unexpected token')) {
        setError('Server error: Invalid response from authentication service. Please try again later.');
      } else {
        setError(error.message || 'An unexpected error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (recaptchaToken: string): Promise<void> => {
    try {
      const response = await fetch(`${BASE_FUNCTION_URL}/create-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userCredentials.email.trim(),
          password: userCredentials.password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role: PORTAL,
          schoolID: null,
          setAsDefault: true,
          recaptchaToken,
        } as CreateUserInput),
      });

      if (!response.ok) {
        const text = await response.text();
        let data: { message?: string } | undefined;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error(`Server error: Invalid response format (${response.status})`);
        }
        const error: FetchError = new Error(data?.message || `Sign-up failed with status ${response.status}`);
        error.response = { data };
        throw error;
      }

      const data: CreateUserOutput = await response.json();
      if (!data.uid) throw new Error(data.message || 'Sign-up failed: No user ID returned');

      const userCredential = await signInWithEmailAndPassword(auth, userCredentials.email, userCredentials.password);
      const user = userCredential.user;
      await user.getIdToken(true);
      const idTokenResult = await user.getIdTokenResult(true);
      const updatedAffiliations = isAffiliationArray(idTokenResult.claims.affiliations)
        ? idTokenResult.claims.affiliations
        : [];
      const defaultAff = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
      if (
        !updatedAffiliations.some((aff) => aff.role === PORTAL && aff.schoolID === null) ||
        !defaultAff ||
        defaultAff.role !== PORTAL ||
        defaultAff.schoolID !== null
      ) {
        await auth.signOut();
        throw new Error(`Failed to set ${PORTAL} role or default affiliation.`);
      }

      localStorage.setItem('portal', PORTAL);
      window.location.href = "/faculty-dashboard";
      setFirstName('');
      setLastName('');
      setUserCredentials({ email: '', password: '' });
      setLoginType('login');
    } catch (e: unknown) {
      const error = e as FetchError;
      error.response = error.response || {};
      throw error;
    }
  };

  const handleSignIn = async (): Promise<void> => {
    try {
      const userCredential: UserCredential = await signInWithEmailAndPassword(auth, userCredentials.email, userCredentials.password);
      const user: User = userCredential.user;
      // Clear stale cached data for different user (AFTER sign-in)
      clearStaleAuthData(user.uid);
      const idTokenResult = await user.getIdTokenResult(true);
      const affiliations = isAffiliationArray(idTokenResult.claims.affiliations)
        ? idTokenResult.claims.affiliations
        : [];
      const defaultAff = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

      const staffAffiliation = affiliations.find((aff) => aff.role === PORTAL);
      const schoolID = staffAffiliation?.schoolID ?? null;

      if (!staffAffiliation) {
        // Add school_staff role if missing
        const setClaims = httpsCallable<SetClaimsInput, SetClaimsOutput>(functions, 'setClaims');
        await setClaims({
          uid: user.uid,
          schoolID: null,
          role: PORTAL,
          setAsDefault: true,
        });
        await user.getIdToken(true);
        const updatedToken = await user.getIdTokenResult(true);
        const updatedAffiliations = isAffiliationArray(updatedToken.claims.affiliations)
          ? updatedToken.claims.affiliations
          : [];
        const updatedDefaultAff = updatedToken.claims.defaultAffiliation as Affiliation | undefined;
        if (
          !updatedAffiliations.some((aff) => aff.role === PORTAL && aff.schoolID === null) ||
          !updatedDefaultAff ||
          updatedDefaultAff.role !== PORTAL ||
          updatedDefaultAff.schoolID !== null
        ) {
          await auth.signOut();
          throw new Error(`Failed to set ${PORTAL} role or default affiliation.`);
        }
      } else if (defaultAff?.role !== PORTAL || defaultAff?.schoolID !== schoolID) {
        // Update defaultAffiliation if incorrect
        const setDefaultAffiliation = httpsCallable(functions, 'setDefaultAffiliation');
        await setDefaultAffiliation({
          uid: user.uid,
          schoolID,
          role: PORTAL,
        });
        await user.getIdToken(true);
        const updatedToken = await user.getIdTokenResult(true);
        const updatedDefaultAff = updatedToken.claims.defaultAffiliation as Affiliation | undefined;
        if (
          !updatedDefaultAff ||
          updatedDefaultAff.role !== PORTAL ||
          updatedDefaultAff.schoolID !== schoolID
        ) {
          await auth.signOut();
          throw new Error(`Failed to set default affiliation to ${PORTAL}.`);
        }
      }

      localStorage.setItem('portal', PORTAL);
      window.location.href = "/faculty-dashboard";
    } catch (e: unknown) {
      throw e as FetchError;
    }
  };

  const handleSocialSignIn = async (provider: GoogleAuthProvider | OAuthProvider, providerName: string): Promise<void> => {
    try {
      // Clear any stale cached auth data BEFORE authentication
      clearStaleAuthData();

      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA is not ready. Please try again later.');
      }
      const recaptchaToken = await executeRecaptcha('social_login');
      const result: UserCredential = await signInWithPopup(auth, provider);
      const user: User = result.user;
      const idToken = await user.getIdToken();
      const idTokenResult = await user.getIdTokenResult(true);
      const affiliations = isAffiliationArray(idTokenResult.claims.affiliations)
        ? idTokenResult.claims.affiliations
        : [];
      const defaultAff = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

      const staffAffiliation = affiliations.find((aff) => aff.role === PORTAL);
      const schoolID = staffAffiliation?.schoolID ?? null;

      if (!staffAffiliation) {
        // Add school_staff role if missing
        const response = await fetch(`${BASE_FUNCTION_URL}/social`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idToken,
            firstName: user.displayName?.split(' ')?.[0] ?? 'N/A',
            lastName: user.displayName?.split(' ')?.slice(1).join(' ') ?? 'N/A',
            provider: providerName,
            role: PORTAL,
            setAsDefault: true,
            schoolID: null,
            recaptchaToken,
          } as CreateUserSocialInput),
        });

        if (!response.ok) {
          const text = await response.text();
          let data: { message?: string } | undefined;
          try {
            data = JSON.parse(text);
          } catch {
            throw new Error(`Server error: Invalid response format (${response.status})`);
          }
          const error: FetchError = new Error(data?.message || `Social sign-in failed with status ${response.status}`);
          error.response = { data };
          throw error;
        }

        const data: CreateUserOutput = await response.json();
        if (!data.uid) throw new Error(data.message || 'Social sign-in failed: No user ID returned');

        await user.getIdToken(true);
        const updatedToken = await user.getIdTokenResult(true);
        const updatedAffiliations = isAffiliationArray(updatedToken.claims.affiliations)
          ? updatedToken.claims.affiliations
          : [];
        const updatedDefaultAff = updatedToken.claims.defaultAffiliation as Affiliation | undefined;
        if (
          !updatedAffiliations.some((aff) => aff.role === PORTAL && aff.schoolID === null) ||
          !updatedDefaultAff ||
          updatedDefaultAff.role !== PORTAL ||
          updatedDefaultAff.schoolID !== null
        ) {
          await auth.signOut();
          throw new Error(`Failed to set ${PORTAL} role or default affiliation.`);
        }
      } else if (defaultAff?.role !== PORTAL || defaultAff?.schoolID !== schoolID) {
        // Update defaultAffiliation if incorrect
        const setDefaultAffiliation = httpsCallable(functions, 'setDefaultAffiliation');
        await setDefaultAffiliation({
          uid: user.uid,
          schoolID,
          role: PORTAL,
        });
        await user.getIdToken(true);
        const updatedToken = await user.getIdTokenResult(true);
        const updatedDefaultAff = updatedToken.claims.defaultAffiliation as Affiliation | undefined;
        if (
          !updatedDefaultAff ||
          updatedDefaultAff.role !== PORTAL ||
          updatedDefaultAff.schoolID !== schoolID
        ) {
          await auth.signOut();
          throw new Error(`Failed to set default affiliation to ${PORTAL}.`);
        }
      }

      localStorage.setItem('portal', PORTAL);
      window.location.href = "/faculty-dashboard";
    } catch (e: unknown) {
      const error = e as FetchError;
      setError(error.message || `${providerName} sign-in failed`);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setResetMessage(null);
    if (!forgotPasswordEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) {
      setError('Invalid email format');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setResetMessage('Password reset email sent. Please check your inbox.');
      setForgotPasswordEmail('');
      setTimeout(() => setShowForgotPassword(false), 3000);
    } catch (e: unknown) {
      const error = e as FetchError;
      setError(error.message || 'Failed to send password reset email.');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      if (user && window.location.pathname.includes('/faculty-login')) {
        try {
          const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
          const affiliations = isAffiliationArray(idTokenResult.claims.affiliations)
            ? idTokenResult.claims.affiliations
            : [];
          const defaultAff = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

          const staffAffiliation = affiliations.find((aff) => aff.role === PORTAL);
          const schoolID = staffAffiliation?.schoolID ?? null;

          if (!staffAffiliation) {
            // Add school_staff role if missing
            const setClaims = httpsCallable<SetClaimsInput, SetClaimsOutput>(functions, 'setClaims');
            await setClaims({
              uid: user.uid,
              schoolID: null,
              role: PORTAL,
              setAsDefault: true,
            });
            await user.getIdToken(true);
            const updatedToken = await user.getIdTokenResult(true);
            const updatedAffiliations = isAffiliationArray(updatedToken.claims.affiliations)
              ? updatedToken.claims.affiliations
              : [];
            const updatedDefaultAff = updatedToken.claims.defaultAffiliation as Affiliation | undefined;
            if (
              !updatedAffiliations.some((aff) => aff.role === PORTAL && aff.schoolID === null) ||
              !updatedDefaultAff ||
              updatedDefaultAff.role !== PORTAL ||
              updatedDefaultAff.schoolID !== null
            ) {
              await auth.signOut();
              setIsLoaded(false);
              throw new Error(`Failed to set ${PORTAL} role or default affiliation.`);
            }
          } else if (defaultAff?.role !== PORTAL || defaultAff?.schoolID !== schoolID) {
            // Update defaultAffiliation if incorrect
            const setDefaultAffiliation = httpsCallable(functions, 'setDefaultAffiliation');
            await setDefaultAffiliation({
              uid: user.uid,
              schoolID,
              role: PORTAL,
            });
            await user.getIdToken(true);
            const updatedToken = await user.getIdTokenResult(true);
            const updatedDefaultAff = updatedToken.claims.defaultAffiliation as Affiliation | undefined;
            if (
              !updatedDefaultAff ||
              updatedDefaultAff.role !== PORTAL ||
              updatedDefaultAff.schoolID !== schoolID
            ) {
              await auth.signOut();
              setIsLoaded(false);
              throw new Error(`Failed to set default affiliation to ${PORTAL}.`);
            }
          }

          localStorage.setItem('portal', PORTAL);
          window.location.href = "/faculty-dashboard";
        } catch (error: unknown) {
          console.error('Error fetching claims:', error);
          setError('Authentication error. Please try again.');
          await auth.signOut();
          setIsLoaded(false);
        }
      } else {
        setIsLoaded(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  return (
    <>
      {isLoaded && <PageLoader isContentReady={false}>{null}</PageLoader>}
      <div className="authe-header">
        <a href="/landing-page">Home</a>
        <a href="#">Features</a>
        <a href="#">Pricing</a>
        <a href="#">About</a>
        <a href="#">Contact Us</a>
      </div>
      <div className="authe-container">
        <form className="authe-form" onSubmit={verifyAndSubmit}>
          <h1 className="authe-heading">{loginType === 'login' ? `Sign In to Sonoma Cloud for ${PORTAL_DISPLAY_NAME}` : `Sign Up to Sonoma Cloud for ${PORTAL_DISPLAY_NAME}`}</h1>
          {loginType === 'signup' && (
            <>
              <label className="authe-label">
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="authe-input"
                  maxLength={50}
                  required
                />
              </label>
              <label className="authe-label">
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="authe-input"
                  maxLength={50}
                  required
                />
              </label>
            </>
          )}
          <label className="authe-label">
            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={userCredentials.email}
              onChange={handleCredentials}
              className="authe-input"
              required
            />
          </label>
          <label className="authe-label">
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={userCredentials.password}
              onChange={handleCredentials}
              className="authe-input"
              minLength={6}
              required
            />
          </label>
          {loginType === 'login' && (
            <h2 className="authe-forgot" onClick={() => setShowForgotPassword(true)} style={{ cursor: 'pointer' }}>
              Forgot Password?
            </h2>
          )}
          <button type="submit" className="authe-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? <div className="spinner"></div> : (loginType === 'login' ? 'Log in' : 'Sign up')}
          </button>
          {error && <p className="authe-error">{error}</p>}
          <h3 className="authe-agreement">
            By signing {loginType === 'login' ? 'in' : 'up'}, I agree to the{' '}
            <Link className="authe-link" to="/privacy-policy">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link className="authe-link" to="/terms-of-service">
              Terms of Service
            </Link>
            .
          </h3>
          <h2 className="authe-or-text">Or {loginType === 'login' ? 'sign in' : 'sign up'} with</h2>
          <div className="authe-social-buttons-container">
            <button
              type="button"
              onClick={() => handleSocialSignIn(new GoogleAuthProvider(), 'Google')}
              className="authe-social-btn-google"
            >
              <img src="https://img.icons8.com/?size=100&id=17949&format=png&color=000000" alt="Google" className="social-icon" />
            </button>
            <button
              type="button"
              onClick={() => handleSocialSignIn(new OAuthProvider('apple.com'), 'Apple')}
              className="authe-social-btn-apple"
            >
              <img src="https://img.icons8.com/?size=100&id=30840&format=png&color=000000" alt="Apple" className="social-icon" />
            </button>
          </div>
          {loginType === 'login' ? (
            <h2 className="authe-bottom-text">
              Don’t have an account? <a href="#" onClick={() => setLoginType('signup')}>
                Sign up
              </a>
            </h2>
          ) : (
            <h2 className="authe-bottom-text">
              Have an account? <a href="#" onClick={() => setLoginType('login')}>
                Sign in
              </a>
            </h2>
          )}
        </form>

        {showForgotPassword && (
          <div className="authe-popup-overlay">
            <div className="authe-popup">
              <h2>Reset Your Password</h2>
              <form onSubmit={handleForgotPassword}>
                <label className="authe-label">
                  <input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="authe-input"
                    required
                  />
                </label>
                {resetMessage && <p className="authe-success">{resetMessage}</p>}
                {error && <p className="authe-error">{error}</p>}
                <div className="authe-popup-btn-container">
                  <button type="submit" className="authe-popup-btn">Send Reset Link</button>
                  <button
                    type="button"
                    className="authe-popup-btn"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setError(null);
                      setResetMessage(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    
      {/* reCAPTCHA Disclaimer Bottom Right */}
      <div className="fixed bottom-4 right-4 z-50 max-w-[200px] text-right pointer-events-none">
        <p className="text-[8px] font-medium text-gray-400 dark:text-gray-500 leading-tight bg-white/80 dark:bg-black/80 backdrop-blur px-2 py-1 rounded">
          Protected by reCAPTCHA.<br/>
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline pointer-events-auto hover:text-black">Privacy</a> &{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline pointer-events-auto hover:text-black">Terms</a> apply.
        </p>
      </div>
</>
  );
};

export default Signup;